import React, { useState, useEffect, useMemo } from 'react';
import { supabase, fetchWithCache, fetchDataWithRelations } from '../lib/supabase';
import { BarChart2, Clock, CheckCircle, InboxIcon } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface DashboardStats {
  neuAuftraege: number;
  inBearbeitungAuftraege: number;
  abgeschlossenAuftraege: number;
}

interface MonteurStats {
  id: string;
  name: string;
  neueAuftraege: number;
  inBearbeitungAuftraege: number;
}

interface CompletedOrder {
  id: string;
  auftragsnummer: string;
  internal_number: string;
  auftragsbezeichnung: string;
  abgeschlossen_am: string;
  monteur: {
    name: string;
  };
  kunde: {
    name: string;
  };
  standort: {
    name: string;
  };
}

function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    neuAuftraege: 0,
    inBearbeitungAuftraege: 0,
    abgeschlossenAuftraege: 0
  });
  const [monteurStats, setMonteurStats] = useState<MonteurStats[]>([]);
  const [completedOrders, setCompletedOrders] = useState<CompletedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  
  // State for data
  const [auftraege, setAuftraege] = useState<any[]>([]);
  const [monteure, setMonteure] = useState<any[]>([]);

  // Calculate stats using memoization
  const calculatedStats = useMemo(() => {
    const neuCount = auftraege.filter(a => a.status === 'neu').length;
    const inBearbeitungCount = auftraege.filter(a => a.status === 'begonnen').length;
    const abgeschlossenCount = auftraege.filter(a => a.status === 'abgeschlossen').length;

    return {
      neuAuftraege: neuCount,
      inBearbeitungAuftraege: inBearbeitungCount,
      abgeschlossenAuftraege: abgeschlossenCount
    };
  }, [auftraege]);

  // Calculate monteur stats efficiently
  const calculatedMonteurStats = useMemo(() => {
    // Calculate stats for all monteurs
    const allMonteurStats = monteure.map(monteur => {
      // Count orders that are new for this technician
      const neueAuftraege = auftraege.filter(
        auftrag => auftrag.monteur_id === monteur.id && auftrag.status === 'neu'
      ).length;
      
      // Count orders that are in progress for this technician
      const inBearbeitungAuftraege = auftraege.filter(
        auftrag => auftrag.monteur_id === monteur.id && auftrag.status === 'begonnen'
      ).length;
      
      return {
        id: monteur.id,
        name: monteur.name,
        neueAuftraege,
        inBearbeitungAuftraege
      };
    });
    
    // Filter to only include monteurs with at least one open order
    const activeMonteurs = allMonteurStats.filter(monteur => 
      monteur.neueAuftraege > 0 || monteur.inBearbeitungAuftraege > 0
    );
    
    // Sort by total number of open orders (descending)
    return activeMonteurs.sort((a, b) => 
      (b.neueAuftraege + b.inBearbeitungAuftraege) - (a.neueAuftraege + a.inBearbeitungAuftraege)
    );
  }, [auftraege, monteure]);

  // Load initial data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // Fetch all necessary data
        const [auftraegeData, monteureData, completedOrdersData] = await Promise.all([
          fetchWithCache('auftraege', () => 
            fetchDataWithRelations('auftraege', '*')
          ),
          fetchWithCache('monteure', () => 
            fetchDataWithRelations('monteure', '*')
          ),
          supabase
            .from('auftraege')
            .select(`
              id,
              auftragsnummer,
              internal_number,
              auftragsbezeichnung,
              abgeschlossen_am,
              monteur:monteure(name),
              kunde:kunden(name),
              standort:standorte(name)
            `)
            .eq('status', 'abgeschlossen')
            .order('abgeschlossen_am', { ascending: false })
            .limit(10)
        ]);

        if (completedOrdersData.error) throw completedOrdersData.error;

        // Update state with fetched data
        setAuftraege(auftraegeData);
        setMonteure(monteureData);
        setCompletedOrders(completedOrdersData.data || []);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Update stats when data changes
  useEffect(() => {
    setStats(calculatedStats);
    setMonteurStats(calculatedMonteurStats);
  }, [calculatedStats, calculatedMonteurStats]);

  // Only include monteurs with active orders in the chart
  const activeMonteurStats = monteurStats.filter(monteur => 
    monteur.neueAuftraege > 0 || monteur.inBearbeitungAuftraege > 0
  );

  const monteurChartData = {
    labels: activeMonteurStats.map(item => item.name),
    datasets: [
      {
        label: 'Neue Aufträge',
        data: activeMonteurStats.map(item => item.neueAuftraege),
        backgroundColor: 'rgba(59, 130, 246, 0.6)', // blue
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'In Bearbeitung',
        data: activeMonteurStats.map(item => item.inBearbeitungAuftraege),
        backgroundColor: 'rgba(234, 179, 8, 0.6)', // yellow
        borderColor: 'rgba(234, 179, 8, 1)',
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Dashboard</h1>
        
        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <p className="text-sm text-gray-500">Neue Aufträge</p>
            </div>
            <p className="text-2xl font-semibold mt-1">{stats.neuAuftraege}</p>
          </div>
          
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <p className="text-sm text-gray-500">In Bearbeitung</p>
            </div>
            <p className="text-2xl font-semibold mt-1">{stats.inBearbeitungAuftraege}</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-sm text-gray-500">Abgeschlossene Aufträge</p>
            </div>
            <p className="text-2xl font-semibold mt-1">{stats.abgeschlossenAuftraege}</p>
          </div>
        </div>

        {/* Admin Inbox - Completed Orders */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <InboxIcon className="h-5 w-5 text-blue-600" />
            Abgeschlossene Aufträge
          </h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auftragsnummer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bezeichnung</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kunde</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standort</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monteur</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Abgeschlossen am</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {completedOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{order.internal_number}</div>
                      <div className="text-sm text-gray-500">{order.auftragsnummer}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{order.auftragsbezeichnung}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.kunde.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.standort.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.monteur.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(order.abgeschlossen_am).toLocaleString('de-DE')}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {completedOrders.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Keine abgeschlossenen Aufträge vorhanden
              </div>
            )}
          </div>
        </div>
        
        {/* Technician Chart */}
        <div className="bg-white border rounded-lg shadow-sm p-4">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <BarChart2 className="h-5 w-5 mr-2 text-yellow-600" />
            Aufträge pro Monteur
          </h2>
          <div className="h-80">
            {activeMonteurStats.length > 0 ? (
              <Bar 
                data={monteurChartData} 
                options={{ 
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      stacked: true,
                      ticks: {
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45
                      }
                    },
                    y: {
                      stacked: true,
                      beginAtZero: true,
                      ticks: {
                        precision: 0
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: true,
                      text: 'Anzahl der Aufträge pro Monteur'
                    },
                    tooltip: {
                      callbacks: {
                        footer: (tooltipItems) => {
                          const datasetIndex = tooltipItems[0].datasetIndex;
                          const index = tooltipItems[0].dataIndex;
                          const monteur = activeMonteurStats[index];
                          const total = monteur.neueAuftraege + monteur.inBearbeitungAuftraege;
                          return `Gesamt: ${total} Aufträge`;
                        }
                      }
                    }
                  }
                }} 
              />
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-gray-500">Keine offenen Aufträge vorhanden</p>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Hinweis: Die Grafik zeigt neue Aufträge (blau) und Aufträge in Bearbeitung (gelb)
          </p>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;